/**
 * Process Log Search Types
 * Shared types for Process Log Search feature (Phase 1)
 */

export const LogSearchType = {
  ORDER: 'order',
  INVENTORY: 'inventory',
  FILE: 'file',
} as const;
export type LogSearchType = (typeof LogSearchType)[keyof typeof LogSearchType];

export type QueryStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** Process log `environment` column values (prod | staging | test). */
export const PROCESS_LOG_ENVIRONMENTS = ['prod', 'staging', 'test'] as const;

export type ProcessLogEnvironment = (typeof PROCESS_LOG_ENVIRONMENTS)[number];

export type SearchField =
  // Order fields
  | 'process_id'
  | 'account_id'
  | 'po_number'
  | 'job_id'
  | 'invoice_id'
  | 'tracking_number'
  // Inventory fields
  | 'sku'
  | 'upc'
  | 'ean'
  | 'gtin'
  // File fields
  | 'filename'
  | 'isa13'
  | 'gs06';

export interface SearchRequest {
  objectType: LogSearchType;
  searchField: SearchField;
  searchValue: string;
  daysBack: number;
  /** Required — row partition (`prod` | `staging` | `test`). */
  environment: ProcessLogEnvironment;
}

export interface SearchResponse {
  queryId: string;
  status: 'queued' | 'running';
  submittedAt: string;
}

export interface ProcessLogResult {
  processId: string;
  accountId: string;
  timestamp: string;
  objectType: LogSearchType;
  data: Record<string, any>;
  keyValueMap?: Record<string, any>;
}

/** Tabular CSV preview from the query-router Athena path (`GET …/v0/status`). */
export interface ProcessLogTabularResultData {
  columns: string[];
  rows: Record<string, string>[];
  truncated?: boolean;
  rowCount?: number;
}

export type ResultInlineOmitReason = 'csv_size' | 'json_size';

export interface StatusResponse {
  queryId: string;
  status: QueryStatus;
  submittedAt: string;
  completedAt?: string;
  results?: ProcessLogResult[];
  /** Athena CSV parsed by the query-router when under inline size limits */
  resultData?: ProcessLogTabularResultData;
  /** Non-fatal issue fetching or parsing inline result data */
  resultDataError?: string;
  /** When true, `resultData` is omitted and `resultPresignedUrl` points at the full CSV */
  resultInlineOmitted?: boolean;
  resultInlineOmitReason?: ResultInlineOmitReason;
  /** Present when `resultInlineOmitted` — time-limited S3 GET URL */
  resultPresignedUrl?: string;
  resultPresignedUrlExpiresAt?: string;
  error?: {
    /** Stable machine-readable reason when the query-router persisted a failure */
    code?: string;
    message: string;
  };
  metadata?: {
    executionId: string;
    rowsScanned?: number;
    executionTimeMs?: number;
  };
}

export interface ProcessLogError {
  code: string;
  message: string;
  details?: any;
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  usingFallback: boolean;
  lastError?: string;
}
